import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import folderService from "@/api/services/folder";
import { getQueryClient } from "@/integrations/tanstack-query/RootProvider";
import { ENDPOINTS } from "@/api/endpoints";
import type { TranscodeJob, TranscodeJobWithDetails } from "@/types/transcode";
import type { QueryKey } from "@tanstack/react-query";
import type { FolderDetails } from "@/types";
import useItemStore from "@/stores/useItem";
import { DEFAULT_TENANT_ID } from "@/config/constants";
import {
  applyStatusToTaskLikeListItem,
  getTaskLikePayloadFromListItem,
  isBackendDrivenTaskLikeItemType,
} from "@/lib/task-like-list-items";
import { ENV } from "@/config/env";

// * Import the endpoints.
const { FOLDER, GROUP_ASSIGNMENT, AI_TASK } = ENDPOINTS;

// * Get the query client.
const { queryClient } = getQueryClient();

/**
 * Poll cadence while the viewed item or any child is a backend-driven task-like item
 * (workflow/epic, AI task, or AI transcode task) still PENDING or IN_PROGRESS.
 */
const BACKEND_DRIVEN_STATUS_POLL_INTERVAL_MS = 7_000;

export const getFolderByIdQueryKey = ({
  id,
  groupId,
}: {
  id?: string | number;
  groupId?: string | null;
}) =>
  ["folder"]
    .concat(id ? [String(id)] : [])
    .concat(groupId ? [String(groupId)] : []) as QueryKey;

/**
 * Get folder by id query options
 * These options provide an overview to the React Query hook
 * and these options can be used separately to get methods of the query hook
 * @param id - Folder id
 * @returns Folder by id query options
 */
const getFolderByIdQueryOptions = (
  id?: string | number,
  groupId?: string | null,
  enabled = true,
) => {
  return queryOptions({
    queryKey: getFolderByIdQueryKey({ id, groupId }),
    queryFn: ({ queryKey: [, id, groupId], signal }) =>
      folderService.getFolderById(String(id), String(groupId), signal),
    enabled:
      !!id &&
      !!groupId &&
      String(groupId) !== "undefined" &&
      enabled &&
      !String(id).startsWith("grouped-tasks-"),
    select: (data) => {
      if (data?.status) {
        const children =
          data.data?.childItems?.sort((a, b) => a?.index - b?.index) || [];
        const res = {
          ...data,
          data: {
            ...data.data,
            childItems: children,
          },
        };
        return res;
      }
    },
    refetchInterval: (query) => {
      const viewedItem = query.state.data?.data;
      const childItems = viewedItem?.childItems ?? [];
      const isBackendWorkInFlight = (item: unknown) => {
        const itemType = (item as { itemType?: string } | undefined)?.itemType;
        if (!isBackendDrivenTaskLikeItemType(itemType)) return false;
        const status = getTaskLikePayloadFromListItem(item)?.status;
        return status === "PENDING" || status === "IN_PROGRESS";
      };
      const hasBackendWorkInFlight =
        isBackendWorkInFlight(viewedItem) ||
        childItems.some((child) => isBackendWorkInFlight(child));
      return hasBackendWorkInFlight
        ? BACKEND_DRIVEN_STATUS_POLL_INTERVAL_MS
        : false;
    },
  });
};

export const getAddNewItemInFolderQueryKey = () =>
  ["add-new-item-in-folder"] as QueryKey;

export const addNewItemInFolderMutation = () =>
  useMutation({
    mutationKey: getAddNewItemInFolderQueryKey(),
    mutationFn: (payload: typeof FOLDER.ADD_NEW_ITEM_IN_FOLDER.requestType) =>
      folderService.addNewItemInFolder(payload),
    onSuccess: (data, { parentId }) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: parentId }),
        });
        return data.data;
      } else {
        throw new Error("Failed to add item");
      }
    },
  });

export const getUpdateChildItemMutationKey = () =>
  ["update-child-item"] as QueryKey;

export const updateChildItemMutation = (fetchAfterSuccess = true) =>
  useMutation({
    mutationKey: getUpdateChildItemMutationKey(),
    mutationFn: (payload: typeof FOLDER.UPDATE_CHILD_ITEM.requestType) =>
      folderService.updateChildItem(payload),
    onSuccess: (data, variables) => {
      if (data?.status && fetchAfterSuccess) {
        const queryKey = getFolderByIdQueryKey({
          id: String(variables?.parentId),
          groupId: String(variables?.groupId),
        });
        // if (variables.childItemType?.toLowerCase() === 'task')
        //   queryClient.removeQueries({ queryKey });
        // else
        if (fetchAfterSuccess) {
          queryClient.invalidateQueries({ queryKey });
        }
        if (variables?.childId) {
          queryClient.invalidateQueries({
            queryKey: getFolderByIdQueryKey({
              id: String(variables?.childId),
              groupId: String(variables?.groupId),
            }),
          });
        }
      }
    },
  });

export const getUpdateParentChildItemMutationKey = (
  id: string | number,
  groupId: string | number,
) => ["update-parent-child-item", String(id), String(groupId)] as QueryKey;
export const updateParentChildItemMutation = (
  id: string | number,
  groupId: string | number,
) => {
  return useMutation({
    mutationKey: getUpdateParentChildItemMutationKey(id, groupId),
    mutationFn: (payload: typeof FOLDER.UPDATE_PARENT_CHILD_ITEM.requestType) =>
      folderService.updateParentChildItem(payload),
    onSuccess: (data, variables) => {
      if (data?.status) {
        // Task updates send list-item id as `parentId` in the payload; folder query key must be
        // the containing folder id (or use store) so the main folder cache actually refreshes.
        const responseData = data.data as
          | { parent?: { id: number } }
          | undefined;
        const parentFromResponse = responseData?.parent?.id;
        const folderIdForInvalidation =
          parentFromResponse != null && String(parentFromResponse) !== ""
            ? parentFromResponse
            : useItemStore.getState().parentId;
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({
            id: String(folderIdForInvalidation),
            groupId: String(variables?.groupId ?? ""),
          }),
        });
      }
    },
  });
};

export const getGroupAssignmentsQueryKey = ({
  groupId,
}: {
  groupId?: string | null;
}) => ["group-assignments", String(groupId ?? "")] as QueryKey;

/** @deprecated Use {@link getGroupAssignmentsQueryKey} */
export const getAllListItemAssignmentsQueryKey = getGroupAssignmentsQueryKey;

/**
 * Fetches group-level editor/subscriber assignments.
 * On group root (`folderId === '-1'`), loads assignments directly by `groupId`.
 * On folder/item views, waits until folder details confirm the item belongs to the active group
 * so we do not call assignments with a stale folder context from another group.
 */
export const useGetGroupAssignments = (
  folderId?: string,
  groupId?: string | null,
  enabled = true,
) => {
  const isGroupRoot = String(folderId) === "-1";
  const itemDetails = useItemStore
    .getState()
    .items.find((item) => String(item.id) === String(folderId));

  // Determine if this ID refers to a temporary/unsaved item in the current view
  const isTempItem = Boolean(
    itemDetails && (itemDetails as any)?.isNew === true,
  );
  const isAddNewPlaceholder =
    String((itemDetails as any)?.itemType || "").toUpperCase() ===
    "ADD_NEW_ITEM";

  const isGroupRootEnabled =
    isGroupRoot && !!groupId && String(groupId) !== "undefined" && enabled;

  const isFolderItemEnabled =
    !!folderId &&
    !!groupId &&
    String(groupId) !== "undefined" &&
    !isGroupRoot &&
    enabled &&
    !isTempItem &&
    !isAddNewPlaceholder &&
    !String(folderId)?.includes("grouped-tasks");

  const folderQuery = useQuery({
    ...getFolderByIdQueryOptions(
      String(folderId),
      groupId,
      isFolderItemEnabled,
    ),
  });

  const folderHierarchyGroupId = folderQuery.data?.data?.hierarchy?.groupId;
  const isFolderScopedToActiveGroup =
    folderQuery.isSuccess &&
    folderHierarchyGroupId != null &&
    String(folderHierarchyGroupId) === String(groupId);

  const isAssignmentsEnabled =
    isGroupRootEnabled || (isFolderItemEnabled && isFolderScopedToActiveGroup);

  return useQuery({
    queryKey: getGroupAssignmentsQueryKey({ groupId }),
    enabled: isAssignmentsEnabled,
    queryFn: ({ signal }) =>
      folderService.getGroupAssignments(groupId || "", signal),
  });
};

/** @deprecated Use {@link useGetGroupAssignments} */
export const useGetAllListItemAssignments = useGetGroupAssignments;

export const getUpdateTaskStatusMutationKey = (taskId?: string) =>
  ["update-task-status", taskId].filter(Boolean) as QueryKey;

export const updateTaskStatusMutation = (taskId?: string) =>
  useMutation({
    mutationKey: getUpdateTaskStatusMutationKey(taskId),
    mutationFn: (
      payload: typeof FOLDER.UPDATE_TASK_STATUS.requestType & {
        taskId?: string | number;
        taskItemId?: string | number;
      },
    ) => folderService.updateTaskStatus(payload),
    onSuccess: (data, { taskId, taskItemId, groupId, status }) => {
      if (data?.status) {
        const idForQuery = taskItemId || taskId;
        const queryKeyOfTask = getFolderByIdQueryKey({
          id: idForQuery,
          groupId,
        });
        const previousData = queryClient.getQueryData(queryKeyOfTask) as {
          data: FolderDetails & Record<string, unknown>;
        };
        if (
          previousData?.data &&
          applyStatusToTaskLikeListItem(previousData.data, status)
        ) {
          queryClient.setQueryData(queryKeyOfTask, {
            ...previousData,
            updatedAt: Date.now(),
          });
        }
        const parentId = data.data?.parent?.id || -1;
        if (parentId && parentId !== -1) {
          const queryKeyOfParent = getFolderByIdQueryKey({
            id: parentId,
            groupId,
          });
          const parentData = queryClient.getQueryData(queryKeyOfParent) as {
            data: FolderDetails;
          };
          if (parentData?.data?.childItems?.length > 0) {
            let changed = false;
            parentData.data.childItems.forEach((child) => {
              if (String(child.id) === String(idForQuery)) {
                if (applyStatusToTaskLikeListItem(child, status)) {
                  changed = true;
                }
              }
            });
            if (changed) {
              queryClient.setQueryData(queryKeyOfParent, {
                ...parentData,
                data: {
                  ...parentData.data,
                  childItems: parentData.data.childItems,
                  updatedAt: Date.now(),
                },
              });
            }
          }
        }
      }
    },
  });

// Transcode APIs
export const getTranscodeMutationKey = () => ["transcode-file"] as QueryKey;
export const transcodeFileMutation = () =>
  useMutation({
    mutationKey: getTranscodeMutationKey(),
    mutationFn: (payload: typeof FOLDER.TRANSCODE_FILE.requestType) =>
      folderService.transcodeFile(payload),
  });

export const getTranscodeJobByIdQueryKey = (
  jobId?: string | number,
  groupId?: string | number,
) => ["transcode-job", String(jobId || ""), String(groupId || "")] as QueryKey;
export const useGetTranscodeJobById = (
  jobId?: string | number,
  groupId?: string | number,
  enabled = true,
) =>
  useQuery({
    queryKey: getTranscodeJobByIdQueryKey(jobId, groupId),
    enabled: Boolean(jobId && groupId && enabled),
    queryFn: async ({ signal }) => {
      const res = await folderService.getTranscodeJobById(
        String(jobId),
        String(groupId),
        signal,
      );
      return res as { status: true; data: TranscodeJobWithDetails };
    },
  });

export const getTranscodeJobsByParentQueryKey = (
  parentId?: string | number,
  groupId?: string | number,
) =>
  ["transcode-jobs", String(parentId || ""), String(groupId || "")] as QueryKey;
export const useGetTranscodeJobsByParent = (
  parentId?: string | number,
  groupId?: string | number,
  enabled = true,
) =>
  useQuery({
    queryKey: getTranscodeJobsByParentQueryKey(parentId, groupId),
    enabled: Boolean(parentId && groupId && enabled),
    queryFn: async ({ signal }) => {
      const res = await folderService.getTranscodeJobsByParent(
        String(parentId),
        String(groupId),
        signal,
      );
      return res as { status: true; data: Array<TranscodeJob> };
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

/**
 * Get all folders query options
 * These options provide an overview to the React Query hook
 * and these options can be used separately to get methods of the query hook
 * @returns All folders query options
 */
export const getAllFolderQueryOptions = queryOptions({
  queryKey: ["folders"],
  queryFn: ({ signal }) => folderService.getAllFolders(signal),
});

/**
 * Get folder by id query
 * @param id - Folder id (required but if not provided, it will not trigger the query)
 * @returns Folder from the api as React Query result
 */
export const useGetFolderById = (
  id?: string | number,
  groupId?: string | null,
  enabled = true,
) => {
  return useQuery(getFolderByIdQueryOptions(String(id), groupId, enabled));
};

/**
 * Get all folders query
 * This query will be triggered when the component is mounted
 * @returns All folders from the api as React Query result
 */
export const useGetAllFolders = () => {
  return useQuery(getAllFolderQueryOptions);
};

export const getDeleteListItemsMutationKey = (
  parentFolderId: string,
  groupId: string,
) => ["delete-list-items", String(parentFolderId), String(groupId)] as QueryKey;

export const deleteListItemsMutation = (
  parentFolderId: string,
  groupId: string,
) =>
  useMutation({
    mutationKey: getDeleteListItemsMutationKey(parentFolderId, groupId),
    mutationFn: (payload: typeof FOLDER.DELETE_LIST_ITEM.requestType) =>
      folderService.deleteListItems(payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: parentFolderId, groupId }),
        });
      }
    },
  });

export const getDuplicateListItemMutationKey = () =>
  ["duplicate-list-item"] as QueryKey;

export const duplicateListItemMutation = () =>
  useMutation({
    mutationKey: getDuplicateListItemMutationKey(),
    mutationFn: (payload: typeof FOLDER.DUPLICATE_LIST_ITEM.requestType) =>
      folderService.duplicateListItem(payload),
    onSuccess: (data, variables) => {
      if (data?.status) {
        // Invalidate target parent folder and source parent if available
        const targetParentId = String(variables?.targetParentId);
        const targetGroupId = String(variables?.targetGroupId);
        if (targetParentId && targetGroupId) {
          queryClient.invalidateQueries({
            queryKey: getFolderByIdQueryKey({
              id: targetParentId,
              groupId: targetGroupId,
            }),
          });
        }
        // Also invalidate source folder if duplicating within same folder
        const sourceGroupId = String(variables?.groupId);
        if (sourceGroupId && variables?.listItemId) {
          // Best effort: refresh current folder view by invalidating its query if equal to target
          if (sourceGroupId === targetGroupId) {
            queryClient.invalidateQueries({
              queryKey: getFolderByIdQueryKey({
                id: targetParentId,
                groupId: sourceGroupId,
              }),
            });
          }
        }
      }
    },
  });

export const getMarkListItemAsReadMutationKey = () =>
  ["mark-list-item-as-read"] as QueryKey;

export const markListItemAsReadMutation = () =>
  useMutation({
    mutationKey: getMarkListItemAsReadMutationKey(),
    mutationFn: (payload: typeof FOLDER.MARK_LIST_ITEM_AS_READ.requestType) =>
      folderService.markListItemAsRead(payload),
    onSuccess: (_data, variables) => {
      // refetchType: 'all' — these queries may currently have no mounted observer (e.g. the
      // caller navigated away from them), and the global `refetchOnMount: false` default means
      // an inactive-but-stale query won't refetch just by remounting later. Force it now so
      // unread indicators are already correct by the time the user navigates back.
      queryClient.invalidateQueries({
        queryKey: getFolderByIdQueryKey({
          id: String(useItemStore.getState().parentId || ""),
          groupId: String(variables.groupId),
        }),
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: getFolderByIdQueryKey({
          id: String(variables.listItemId),
          groupId: String(variables.groupId),
        }),
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["groups"],
        exact: false,
      });
    },
  });

/**
 * Get move items mutation key
 * @param sourceFolderId - The source folder ID where items are currently located
 * @param targetFolderId - The target folder ID where items will be moved
 * @param groupId - The group ID for cache invalidation
 * @returns Query key for the move items mutation
 */
export const getMoveItemsMutationKey = (sourceFolderId: string) =>
  ["move-items", String(sourceFolderId)] as QueryKey;

/**
 * Move items mutation hook
 * @param sourceFolderId - The source folder ID where items are currently located
 * @param targetFolderId - The target folder ID where items will be moved
 * @param groupId - The group ID for cache invalidation
 * @returns Mutation hook for moving items between folders
 */
export const moveItemsMutation = (sourceFolderId?: string) => {
  return useMutation({
    mutationKey: getMoveItemsMutationKey(sourceFolderId!),
    mutationFn: (payload: typeof FOLDER.MOVE_ITEMS.requestType) =>
      folderService.moveItems(payload),
    onSuccess: (data, { groupId, newParentId, targetGroupId, itemListIds }) => {
      if (data?.status) {
        // Optimistically remove moved items from source folder cache for snappier UI
        const sourceKey = getFolderByIdQueryKey({
          id: String(sourceFolderId),
          groupId: String(groupId),
        });
        const prev = queryClient.getQueryData(sourceKey) as
          | { data: FolderDetails }
          | undefined;
        if (prev?.data?.childItems?.length) {
          const removed = new Set((itemListIds || []).map((id) => String(id)));
          const nextChildren = prev.data.childItems.filter(
            (ci) => !removed.has(String(ci.id)),
          );
          if (nextChildren.length !== prev.data.childItems.length) {
            queryClient.setQueryData(sourceKey, {
              ...prev,
              data: {
                ...prev.data,
                childItems: nextChildren,
                updatedAt: Date.now(),
              },
            });
          }
        }
        // Invalidate both source and target folder queries to ensure eventual consistency
        queryClient.invalidateQueries({ queryKey: sourceKey });
        // If cross-group, invalidate with targetGroupId for target parent; otherwise same group
        const invalidateTargetGroupId = targetGroupId || groupId;
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({
            id: newParentId,
            groupId: invalidateTargetGroupId,
          }),
        });
      }
    },
  });
};

/**
 * Add subscriber at group level.
 * @param folderId - Folder ID for cache invalidation of folder details
 * @param groupId - Group ID for assignments and cache invalidation
 */
export const getAddGroupSubscriberMutationKey = (
  folderId: string,
  groupId: string,
) => ["add-group-subscriber", String(folderId), String(groupId)] as QueryKey;

export const addGroupSubscriberMutation = (folderId: string, groupId: string) =>
  useMutation({
    mutationKey: getAddGroupSubscriberMutationKey(folderId, groupId),
    mutationFn: (payload: typeof GROUP_ASSIGNMENT.ADD_SUBSCRIBER.requestType) =>
      folderService.addSubscriberToGroup(payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getGroupAssignmentsQueryKey({ groupId }),
        });
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: folderId, groupId }),
        });
      }
    },
  });

/** @deprecated Use {@link addGroupSubscriberMutation} */
export const addSubscriberToListItemMutation = addGroupSubscriberMutation;

/**
 * Remove subscriber at group level.
 * @param folderId - Folder ID for cache invalidation of folder details
 * @param groupId - Group ID for assignments and cache invalidation
 */
export const getRemoveGroupSubscriberMutationKey = (
  folderId: string,
  groupId: string,
) => ["remove-group-subscriber", String(folderId), String(groupId)] as QueryKey;

export const removeGroupSubscriberMutation = (
  folderId: string,
  groupId: string,
) =>
  useMutation({
    mutationKey: getRemoveGroupSubscriberMutationKey(folderId, groupId),
    mutationFn: (
      payload: typeof GROUP_ASSIGNMENT.REMOVE_SUBSCRIBER.requestType,
    ) => folderService.removeSubscriberFromGroup(payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getGroupAssignmentsQueryKey({ groupId }),
        });
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: folderId, groupId }),
        });
      }
    },
  });

/** @deprecated Use {@link removeGroupSubscriberMutation} */
export const removeSubscriberFromListItemMutation =
  removeGroupSubscriberMutation;

/**
 * Add editor at group level.
 * @param folderId - Folder ID for cache invalidation of folder details
 * @param groupId - Group ID for assignments and cache invalidation
 */
export const getAddGroupEditorMutationKey = (
  folderId: string,
  groupId: string,
) => ["add-group-editor", String(folderId), String(groupId)] as QueryKey;

export const addGroupEditorMutation = (folderId: string, groupId: string) =>
  useMutation({
    mutationKey: getAddGroupEditorMutationKey(folderId, groupId),
    mutationFn: (payload: typeof GROUP_ASSIGNMENT.ADD_EDITOR.requestType) =>
      folderService.addEditorToGroup(payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getGroupAssignmentsQueryKey({ groupId }),
        });
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: folderId, groupId }),
        });
      }
    },
  });

/** @deprecated Use {@link addGroupEditorMutation} */
export const addEditorToListItemMutation = addGroupEditorMutation;

/**
 * Remove editor at group level.
 * @param folderId - Folder ID for cache invalidation of folder details
 * @param groupId - Group ID for assignments and cache invalidation
 */
export const getRemoveGroupEditorMutationKey = (
  folderId: string,
  groupId: string,
) => ["remove-group-editor", String(folderId), String(groupId)] as QueryKey;

export const removeGroupEditorMutation = (folderId: string, groupId: string) =>
  useMutation({
    mutationKey: getRemoveGroupEditorMutationKey(folderId, groupId),
    mutationFn: (payload: typeof GROUP_ASSIGNMENT.REMOVE_EDITOR.requestType) =>
      folderService.removeEditorFromGroup(payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getGroupAssignmentsQueryKey({ groupId }),
        });
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: folderId, groupId }),
        });
      }
    },
  });

/** @deprecated Use {@link removeGroupEditorMutation} */
export const removeEditorFromListItemMutation = removeGroupEditorMutation;

const API_BASE_URL = ENV.API_URL;
const HOST_PREFIX_URL_PATTERN = /^[\w.-]+\.[a-z]{2,}(?::\d+)?(?:\/|$)/i;

/**
 * Resolves a relative media path or absolute URL to a fetchable URL.
 */
function resolveMediaUrl(url: string): string {
  if (!url) {
    return "";
  }
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }
  if (/^[a-z][a-z\d+\-.]*:\/\//i.test(url) || url.startsWith("//")) {
    return url;
  }
  if (HOST_PREFIX_URL_PATTERN.test(url)) {
    return `https://${url}`;
  }
  if (!API_BASE_URL) {
    return url;
  }
  return `${API_BASE_URL}${url}`;
}

/**
 * Returns true when the image should load via `<img src>` without a cross-origin fetch.
 * GCS signed URLs and other external hosts fail CORS when fetched with API headers.
 */
function shouldLoadImageDirectly(fullUrl: string): boolean {
  if (fullUrl.startsWith("data:")) {
    return true;
  }
  if (fullUrl.includes("storage.googleapis.com")) {
    return true;
  }
  if (!API_BASE_URL || !fullUrl.startsWith("http")) {
    return false;
  }
  try {
    return new URL(fullUrl).origin !== new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
}

/**
 * Loads image data for display: external URLs (e.g. GCS) are returned as-is for `<img src>`;
 * same-origin API paths are fetched and converted to data URLs.
 */
export const useImageData = (url: string, enabled = true) => {
  return useQuery({
    queryKey: ["image-data", url],
    queryFn: async (): Promise<string> => {
      const fullUrl = resolveMediaUrl(url);

      if (shouldLoadImageDirectly(fullUrl)) {
        return fullUrl;
      }

      const response = await fetch(fullUrl, {
        headers: {
          "X-Tenant-Id": DEFAULT_TENANT_ID,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    },
    enabled: enabled && !!url,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

export const getExecuteAiTaskMutationKey = (
  aiTaskListItemId: string,
  groupId: string,
) => ["execute-ai-task", String(aiTaskListItemId), String(groupId)] as QueryKey;

export const executeAiTaskMutation = (
  aiTaskListItemId: string,
  groupId: string,
) =>
  useMutation({
    mutationKey: getExecuteAiTaskMutationKey(aiTaskListItemId, groupId),
    mutationFn: (payload: typeof AI_TASK.EXECUTE_AI_TASK.requestType) =>
      folderService.executeAiTask(payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getGetAiTaskResultQueryKey(aiTaskListItemId, groupId),
        });
      }
    },
  });

export const getGetAiTaskResultQueryKey = (
  aiTaskListItemId: string,
  groupId: string,
) => ["ai-task-result", String(aiTaskListItemId), String(groupId)] as QueryKey;

export const useGetAiTaskResult = (
  aiTaskListItemId: string,
  groupId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: getGetAiTaskResultQueryKey(aiTaskListItemId, groupId),
    queryFn: ({ signal }) =>
      folderService.getAiTaskResult(
        { aiTaskListItemId: Number(aiTaskListItemId), groupId },
        signal,
      ),
    enabled:
      !!aiTaskListItemId &&
      !!groupId &&
      String(groupId) !== "undefined" &&
      enabled,
  });

export const getLinkListItemsToAiTaskMutationKey = (
  aiTaskListItemId: string,
  groupId: string,
) =>
  [
    "link-list-items-to-ai-task",
    String(aiTaskListItemId),
    String(groupId),
  ] as QueryKey;

export const linkListItemsToAiTaskMutation = (
  aiTaskListItemId: string,
  groupId: string,
) =>
  useMutation({
    mutationKey: getLinkListItemsToAiTaskMutationKey(aiTaskListItemId, groupId),
    mutationFn: (payload: typeof AI_TASK.LINK_LIST_ITEMS.requestType) =>
      folderService.linkListItemsToAiTask(aiTaskListItemId, payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: aiTaskListItemId, groupId }),
        });
      }
    },
  });

export const getDelinkListItemsFromAiTaskMutationKey = (
  aiTaskListItemId: string,
  groupId: string,
) =>
  [
    "delink-list-items-from-ai-task",
    String(aiTaskListItemId),
    String(groupId),
  ] as QueryKey;

export const delinkListItemsFromAiTaskMutation = (
  aiTaskListItemId: string,
  groupId: string,
) =>
  useMutation({
    mutationKey: getDelinkListItemsFromAiTaskMutationKey(
      aiTaskListItemId,
      groupId,
    ),
    mutationFn: (payload: typeof AI_TASK.DELINK_LIST_ITEMS.requestType) =>
      folderService.delinkListItemsFromAiTask(aiTaskListItemId, payload),
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({
          queryKey: getFolderByIdQueryKey({ id: aiTaskListItemId, groupId }),
        });
      }
    },
  });
